/**
 * @fileoverview Savings Dashboard Component
 * @description Month-wise chart plus KPI hero cards showing:
 *   a. Tests Generated  b. Hours Saved  c. Dollar Savings
 *   Grouped by AI source (Test Generator, MCP, GHCP, M365, etc.)
 */

import React, { useMemo } from 'react';
import { Box, Paper, Typography, Chip, Tooltip } from '@mui/material';
import {
  FlaskConical,
  Clock,
  DollarSign,
  TrendingUp,
  Calendar,
  Zap,
} from 'lucide-react';

/* ───────────────────────── colour palette ───────────────────────── */

const SOURCE_COLORS = {
  Yes:       '#0097A9',
  No:        '#E5E7EB',
  MCP:       '#6366F1',
  GHCP:      '#059669',
  M365:      '#D97706',
  'Test Generator': '#7C3AED',
  Other:     '#94A3B8',
};
const getSourceColor = (src) => SOURCE_COLORS[src] || SOURCE_COLORS.Other;

const MONTH_NAMES = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const fmtMonth = (key) => {
  // key = "2026-04"
  const [y, m] = key.split('-');
  return `${MONTH_NAMES[parseInt(m, 10)]} ${y}`;
};

const fmtDollar = (v) =>
  v >= 1000
    ? `$${(v / 1000).toFixed(1)}K`
    : `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/* ───────────────────────── Hero KPI Card ───────────────────────── */

const HeroCard = ({ icon: Icon, label, value, sub, color, gradient }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 220,
      p: 3,
      borderRadius: 3,
      background: gradient,
      border: '1px solid rgba(255,255,255,0.25)',
      color: '#fff',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* watermark icon */}
    <Icon
      size={80}
      style={{
        position: 'absolute',
        right: -12,
        bottom: -12,
        opacity: 0.10,
      }}
    />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Icon size={20} />
      <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9 }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: 'block' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

/* ───────────────────────── Stacked Bar Chart ─────────────────────── */

const StackedBarChart = ({ monthly, sources, maxTests }) => {
  const barMax = Math.max(maxTests, 1);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 220, mt: 2 }}>
      {monthly.map((m) => {
        const pct = (m.testsGenerated / barMax) * 100;
        const srcEntries = Object.entries(m.bySource || {});

        return (
          <Tooltip
            key={m.month}
            arrow
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {fmtMonth(m.month)}
                </Typography>
                <Typography variant="body2">
                  Tests: <b>{m.testsGenerated}</b>
                </Typography>
                <Typography variant="body2">
                  Hours Saved: <b>{m.hoursSaved}</b>
                </Typography>
                <Typography variant="body2">
                  Savings: <b>{fmtDollar(m.dollarSaved)}</b>
                </Typography>
                {srcEntries.map(([s, c]) => (
                  <Typography key={s} variant="caption" sx={{ display: 'block', mt: 0.3 }}>
                    • {s}: {c}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                height: '100%',
                minWidth: 36,
              }}
            >
              {/* stacked segments */}
              <Box
                sx={{
                  width: '70%',
                  maxWidth: 52,
                  height: `${Math.max(pct, 3)}%`,
                  borderRadius: '6px 6px 2px 2px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  transition: 'height 0.4s ease',
                  cursor: 'pointer',
                }}
              >
                {srcEntries.map(([src, cnt]) => {
                  const segPct = m.testsGenerated > 0 ? (cnt / m.testsGenerated) * 100 : 0;
                  return (
                    <Box
                      key={src}
                      sx={{
                        height: `${segPct}%`,
                        backgroundColor: getSourceColor(src),
                        minHeight: 2,
                      }}
                    />
                  );
                })}
              </Box>
              {/* count label */}
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: '#374151', mt: 0.5 }}
              >
                {m.testsGenerated}
              </Typography>
              {/* month label */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: '#9CA3AF',
                  whiteSpace: 'nowrap',
                }}
              >
                {fmtMonth(m.month)}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

/* ───────────────────────── Main Component ───────────────────────── */

const SavingsDashboard = ({ metrics }) => {
  const totals = metrics?.totals;
  const monthly = metrics?.monthly;
  const bySources = metrics?.bySources;

  const maxTests = useMemo(
    () => Math.max(...(monthly || []).map((m) => m.testsGenerated), 0),
    [monthly]
  );

  const allSources = useMemo(
    () => Object.keys(bySources || {}),
    [bySources]
  );

  if (!metrics) return null;

  return (
    <Box sx={{ mb: 3 }}>
      {/* ── Hero KPI cards ───────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <HeroCard
          icon={FlaskConical}
          label="AI-Generated Tests"
          value={totals.aiGenerated.toLocaleString()}
          sub={`${totals.testsGenerated.toLocaleString()} total test cases tracked`}
          color="#0097A9"
          gradient="linear-gradient(135deg, #01426A 0%, #0097A9 100%)"
        />
        <HeroCard
          icon={Clock}
          label="Hours Saved"
          value={totals.hoursSaved.toLocaleString()}
          sub={`@ ${metrics.hoursPerTest} hr/test`}
          color="#059669"
          gradient="linear-gradient(135deg, #065F46 0%, #059669 100%)"
        />
        <HeroCard
          icon={DollarSign}
          label="Cost Savings"
          value={fmtDollar(totals.dollarSaved)}
          sub={`@ $${metrics.hourlyRate}/hr`}
          color="#7C3AED"
          gradient="linear-gradient(135deg, #5B21B6 0%, #7C3AED 100%)"
        />
      </Box>

      {/* ── Monthly Chart ────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calendar size={20} color="#01426A" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A' }}>
              Monthly Test Generation
            </Typography>
          </Box>

          {/* source legend */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {allSources.map((src) => (
              <Chip
                key={src}
                label={`${src}: ${bySources[src]}`}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: `${getSourceColor(src)}18`,
                  color: getSourceColor(src),
                  border: `1px solid ${getSourceColor(src)}40`,
                }}
              />
            ))}
          </Box>
        </Box>

        {monthly && monthly.length > 0 ? (
          <StackedBarChart
            monthly={monthly}
            sources={allSources}
            maxTests={maxTests}
          />
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
            No monthly data available.
          </Typography>
        )}

        {/* Cumulative footer */}
        {monthly && monthly.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 3,
              mt: 2,
              pt: 2,
              borderTop: '1px solid #F3F4F6',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp size={14} color="#0097A9" />
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Cumulative:{' '}
                <b style={{ color: '#1F2937' }}>
                  {monthly[monthly.length - 1].cumulativeTests} tests
                </b>{' '}
                ·{' '}
                <b style={{ color: '#059669' }}>
                  {monthly[monthly.length - 1].cumulativeHours} hrs
                </b>{' '}
                ·{' '}
                <b style={{ color: '#7C3AED' }}>
                  {fmtDollar(monthly[monthly.length - 1].cumulativeSavings)}
                </b>
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SavingsDashboard;
